import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { DataJob } from '@cloudsign/shared';
import type { DataSubject, ErasurePreview } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import { Alert, Button, Card, TableWrap, TextField, Th } from '../../components/ui';
import { ErrorLine } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { jobsKey, postJob } from './api';

/** What an erasure would remove and what stays (and why), then a typed confirmation. */
export function ErasePanel({
  subject,
  preview,
  reason,
  onCancel,
  onStarted,
}: {
  subject: DataSubject;
  preview: ErasurePreview;
  reason: string;
  onCancel: () => void;
  onStarted: (job: DataJob) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const word = t('p4dp.erase.confirmWord');
  const [typed, setTyped] = useState('');
  const start = useMutation({
    mutationFn: () => postJob('/data-protection/erasures', { subject, reason, requestId: null, confirm: true }),
    onSuccess: (job) => {
      void qc.invalidateQueries({ queryKey: jobsKey });
      toast(t('p4dp.erase.started'));
      onStarted(job);
    },
  });
  const ok = typed.trim() === word;
  return (
    <Card
      title={t('p4dp.erase.title')}
      description={t('p4dp.erase.help')}
      footer={
        <>
          <Button onClick={onCancel}>{t('p4dp.erase.cancel')}</Button>
          <Button variant="danger" disabled={!ok || start.isPending} onClick={() => start.mutate()}>
            {t('p4dp.erase.confirmButton')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {preview.holds.length > 0 ? (
          <Alert kind="error">
            <strong className="block">{t('p4dp.erase.holdsTitle')}</strong>
            <span className="block">{t('p4dp.erase.holdsHelp')}</span>
            <ul className="mt-1 list-disc pl-5">
              {preview.holds.map((h) => (
                <li key={h.id}>{h.label}</li>
              ))}
            </ul>
          </Alert>
        ) : null}
        <TableWrap caption={t('p4dp.erase.title')}>
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <Th>{t('p4dp.erase.module')}</Th>
              <Th>{t('p4dp.erase.erased')}</Th>
              <Th>{t('p4dp.erase.anonymised')}</Th>
              <Th>{t('p4dp.erase.kept')}</Th>
              <Th>{t('p4dp.erase.why')}</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {preview.modules.map((m) => (
              <tr key={m.module}>
                <td className="px-4 py-2.5 font-medium">{m.module}</td>
                <td className="px-4 py-2.5">{m.erased}</td>
                <td className="px-4 py-2.5">{m.anonymised}</td>
                <td className="px-4 py-2.5">{m.kept}</td>
                <td className="px-4 py-2.5 text-slate-700">{m.keepReasonKey ? t(m.keepReasonKey) : ''}</td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
        <TextField label={t('p4dp.erase.confirmLabel', { word })} value={typed} autoComplete="off" onChange={(e) => setTyped(e.target.value)} />
        <ErrorLine error={start.error} />
      </div>
    </Card>
  );
}
